/**
 * Telemetry — captures play events for tuning.
 *
 * Three sinks at once:
 *  1. In-memory ring (256 events) — for live `window.Telemetry.recent()`.
 *  2. localStorage event log — persistent across reloads, downloadable via
 *     `window.Telemetry.export()`. Hard-capped at 5000 events.
 *  3. Network POST batches to /api/telemetry every 10s when consent is on.
 *
 * Naming convention: `domain.action[.detail]` e.g. `run.start`, `combat.kill`.
 * Events with a `where` prop (depth/biome/distance) are most useful for
 * answering "where do runs die?" — include it whenever possible.
 */
export interface TelemetryEvent {
  ts: number;
  name: string;
  props?: Record<string, string | number | boolean>;
}

const RING_SIZE = 256;
const STORAGE_KEY = 'wanderborne.telemetry.v1';
const STORAGE_MAX = 5000;
const FLUSH_INTERVAL_MS = 10_000;
const ENDPOINT = '/api/telemetry';

const ring: TelemetryEvent[] = [];
let consentGiven = false;
let initialized = false;
let sessionId = makeSessionId();
let flushTimer: ReturnType<typeof setInterval> | null = null;
let unflushed: TelemetryEvent[] = [];

function makeSessionId(): string {
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function loadStored(): TelemetryEvent[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch { return []; }
}

function persistOne(e: TelemetryEvent): void {
  try {
    const stored = loadStored();
    stored.push(e);
    while (stored.length > STORAGE_MAX) stored.shift();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch { /* ignore */ }
}

async function flushNetwork(): Promise<void> {
  if (!consentGiven || unflushed.length === 0) return;
  const batch = unflushed;
  unflushed = [];
  try {
    await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ session: sessionId, sentAt: Date.now(), events: batch }),
      keepalive: true,
    });
  } catch {
    // Restore so we retry on next interval.
    unflushed = [...batch, ...unflushed];
  }
}

export const Telemetry = {
  init(opts: { consent: boolean }): void {
    consentGiven = opts.consent;
    initialized = true;
    if (flushTimer) clearInterval(flushTimer);
    if (consentGiven) {
      flushTimer = setInterval(flushNetwork, FLUSH_INTERVAL_MS);
      // Flush on page hide so we don't lose the tail of a session.
      addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushNetwork();
      });
    }
  },

  log(name: string, props?: TelemetryEvent['props']): void {
    try {
      const e: TelemetryEvent = { ts: performance.now(), name, props };
      ring.push(e);
      if (ring.length > RING_SIZE) ring.shift();
      persistOne(e);
      if (initialized && consentGiven) unflushed.push(e);
    } catch { /* never throw from telemetry */ }
  },

  /** Inspect recent events — exposed on window for live debugging. */
  recent(): readonly TelemetryEvent[] { return ring; },

  /** Pull the full persisted log (across reloads). */
  exportAll(): TelemetryEvent[] { return loadStored(); },

  /** Force-download the full log as JSON. Useful for sharing playtests. */
  download(): void {
    const data = JSON.stringify(loadStored(), null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wanderborne-telemetry-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  /** Wipe the persisted log (testing helper). */
  clear(): void {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  },

  sessionId(): string { return sessionId; },
  startNewSession(): void { sessionId = makeSessionId(); },
};

// Expose for browser-console inspection.
(window as unknown as { Telemetry: typeof Telemetry }).Telemetry = Telemetry;
